import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Form, Row, Col, Divider, Button, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { LorawanActionTypes } from '@utils/deviceActions';
import SessionContext from '@providers/Session/SessionContext';
import { useNavigate } from 'react-router-dom';
import api from '@services/api';
import DetailsStep from './ActivationModalSteps/DetailsStep';
import { DeviceProps } from '.';
import * as S from './styles';

type TokenProps = {
  description: string;
  tokenActivation: string;
};

type DetailsProps = {
  requestId?: number;
  contractId?: string;
  contractMotId?: string;
  contractLorawanId?: string;
};

type DeviceTokenProps = {
  devEui?: string;
  description?: string;
};

interface ActivationPayloadProps {
  action: LorawanActionTypes.ACTIVATION;
  tokenActivation: string;
  contractId?: string;
  accountId?: number;
  contractIdMOT?: string;
  devices: DeviceTokenProps[];
}

interface ActivationTokenProps {
  open: boolean;
  onClose: () => void;
  renderItemLabel: (label: string) => JSX.Element;
  renderInputItem: (
    label: string,
    numSize?: number,
    validateNumSize?: boolean,
  ) => JSX.Element;
}

const ActivationToken = ({
  open,
  onClose,
  renderItemLabel,
  renderInputItem,
}: ActivationTokenProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('lorawan');
  const { selectedApp, isMot } = useContext(SessionContext);

  const [tokenForm] = Form.useForm();
  const [detailsForm] = Form.useForm();

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [uploadedDevices, setUploadedDevices] = useState<DeviceProps[]>();

  const postActivation = useCallback(
    (payload: ActivationPayloadProps) =>
      api.post('/service-proxy/lorawan/actions', payload),
    [],
  );

  const handleSuccess = useCallback(() => {
    const navigatePath = `/${selectedApp}${
      isMot ? '/lorawan' : ''
    }/operational/actions-queue?actionType=0`;
    message.success(
      <>
        <span>{t('device-actions-modal:msgs.requested-action')}</span>
        <Button
          type="link"
          style={{ margin: '0 4px', padding: 0 }}
          onClick={() => navigate(navigatePath)}
        >
          {t('device-actions-modal:msgs.follow-in-the-actions-queue')}
        </Button>
      </>,
      2,
    );
  }, [navigate, selectedApp, isMot, t]);

  const handleClose = useCallback(() => {
    tokenForm.resetFields();
    detailsForm.resetFields();
    setUploadedDevices(undefined);
    onClose();
  }, [detailsForm, onClose, tokenForm]);

  const handleFinish = useCallback(
    async (payload: ActivationPayloadProps) => {
      setConfirmLoading(true);
      try {
        await postActivation(payload);
        handleSuccess();
        handleClose();
      } catch {
        message.error(t('errorWhenActivating'));
      }
      setConfirmLoading(false);
    },
    [handleClose, handleSuccess, postActivation, t],
  );

  const devices = useMemo(
    (): DeviceTokenProps[] =>
      uploadedDevices?.map(dev => ({
        devEui: dev.devEui,
        description: dev.description,
      })) ?? [],
    [uploadedDevices],
  );

  const handlePayload = useCallback(
    (
      tokenData: TokenProps,
      detailsData: DetailsProps,
    ): ActivationPayloadProps => ({
      action: LorawanActionTypes.ACTIVATION,
      accountId: detailsData?.requestId,
      contractId: isMot
        ? detailsData?.contractLorawanId
        : detailsData?.contractId,
      contractIdMOT: detailsData?.contractMotId,
      devices,
      ...tokenData,
    }),
    [devices, isMot],
  );

  const handleConfirm = useCallback(
    () =>
      tokenForm
        .validateFields()
        .then(tokenData => tokenData)
        .then(tokenData => {
          detailsForm.validateFields().then(detailsData => {
            handleFinish(handlePayload(tokenData, detailsData));
          });
        }),
    [detailsForm, handleFinish, handlePayload, tokenForm],
  );

  return (
    <S.ModalContainer
      bodyStyle={{ padding: '24px 40px' }}
      footer={
        <Button type="primary" onClick={handleConfirm} loading={confirmLoading}>
          {t('buttons.confirm')}
        </Button>
      }
      maskClosable={false}
      title={t('tokenActivation')}
      open={open}
      centered
      forceRender
      onCancel={handleClose}
    >
      <Form
        colon={false}
        form={tokenForm}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        scrollToFirstError
        layout="vertical"
      >
        <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Form.Item
              label="Token"
              name="tokenActivation"
              rules={[
                {
                  required: true,
                  message: t('fieldIsRequired'),
                },
              ]}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Divider />

      <DetailsStep
        form={detailsForm}
        isBatch
        isToken
        onUpload={setUploadedDevices}
        renderItemLabel={renderItemLabel}
        renderInputItem={renderInputItem}
      />
    </S.ModalContainer>
  );
};

export default ActivationToken;
